// src/components/shared/MicrosoftAuth.js
import React, { useContext, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { ConfigContext } from './ConfigContext';
import { setTokens, scheduleProactiveRefresh } from '../../utils/authTokens';
import { radius } from '../../themes/smartinventoryTheme';

// Cache instance by clientId+authority to avoid recreating across renders
const msalInstances = {};
const msalInitPromises = {};

export const persistMicrosoftUser = (user, setAuth, backendUrl) => {
    localStorage.setItem('userId', user._id);
    localStorage.setItem('userRole', user.role);
    localStorage.setItem('username', user.username);
    localStorage.setItem('companyId', user.companyId);
    setTokens({ accessToken: user.accessToken, refreshToken: user.refreshToken });
    scheduleProactiveRefresh(backendUrl);
    setAuth?.({
        username: user.username,
        userRole: user.role,
    });
    // `companies` is only present when the login route detected more than one
    // membership — the caller is already validly signed into a default
    // company above, this just asks them to confirm/pick before landing on
    // the dashboard.
    return user.companies && user.companies.length > 1 ? '/select-company' : '/';
};

const isHttpsUrl = (value) => typeof value === 'string' && /^https:\/\//i.test(value);

export const resolveAuthority = (config) => {
    // Highest priority: explicit authority, but only if it's a proper HTTPS URL
    if (config?.azureEntraAuthority) {
        if (isHttpsUrl(config.azureEntraAuthority)) {
            return config.azureEntraAuthority;
        } else {
            console.warn('Ignoring azureEntraAuthority: must be an HTTPS URL like https://login.microsoftonline.com/<tenant>.');
        }
    }

    // Next: tenant id. Accept either a tenant identifier (guid/domain) or a full https authority URL
    if (config?.azureEntraTenantId) {
        if (isHttpsUrl(config.azureEntraTenantId)) {
            return config.azureEntraTenantId;
        }
        return `https://login.microsoftonline.com/${config.azureEntraTenantId}`;
    }

    // Fallback: multi-tenant common
    return 'https://login.microsoftonline.com/common';
};

const getMsalInstance = (clientId, authority) => {
    if (!clientId) return null;
    const key = `${clientId}|${authority}`;
    if (!msalInstances[key]) {
        msalInstances[key] = new PublicClientApplication({
            auth: {
                clientId,
                authority,
                redirectUri: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: false,
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level, message) => {
                        // Helpful during integration; keep console logs minimal
                        if (level === 3) { // Error
                            console.error('[MSAL]', message);
                        }
                    },
                    piiLoggingEnabled: false,
                },
            },
        });
    }
    return msalInstances[key];
};

const getInitializedMsalInstance = async (clientId, authority) => {
    const key = `${clientId}|${authority}`;
    const instance = getMsalInstance(clientId, authority);
    if (!instance) return null;
    if (!msalInitPromises[key]) {
        msalInitPromises[key] = instance.initialize();
    }
    await msalInitPromises[key];
    return instance;
};

const MicrosoftAuth = ({ setAuth }) => {
    const { t } = useTranslation();
    const config = useContext(ConfigContext);
    const [busy, setBusy] = useState(false);

    if (!config || !config.backendUrl) {
        return <Button variant="outlined" fullWidth disabled sx={{ borderRadius: `${radius.pill}px`, height: 48 }}>{t('Loading configuration...', { defaultValue: 'Loading configuration...' })}</Button>;
    }

    const handleMicrosoftLogin = async () => {
        try {
            if (busy) return; // Guard against double-clicks / concurrent calls
            setBusy(true);
            const authority = resolveAuthority(config);
            const inst = await getInitializedMsalInstance(config.azureEntraClientId, authority);
            if (!inst) {
                alert('Azure Entra client ID is missing from configuration');
                return;
            }

            const loginRequest = { scopes: ['openid', 'profile', 'email'], prompt: 'select_account' };
            const result = await inst.loginPopup(loginRequest);
            const idToken = result.idToken;

            const apiBase = (config.backendUrl || '').replace(/\/$/, '');
            const response = await axios.post(`${apiBase}/auth/microsoft`, {
                token: idToken,
            });

            const user = response.data;
            window.location.href = persistMicrosoftUser(user, setAuth, config.backendUrl);
        } catch (err) {
            console.error('Microsoft Sign-In error:', err);
            const code = err?.errorCode || '';
            const msalMessage = err?.errorMessage || err?.message;
            if (code === 'interaction_in_progress' || /Interaction is currently in progress/i.test(msalMessage || '')) {
                alert('A Microsoft login is already in progress. Please close any open login popups or wait for it to complete, then try again. If the issue persists, refresh the page.');
            } else {
                const friendly = err?.response?.data?.error || msalMessage || 'Error during Microsoft login.';
                alert(friendly);
            }
        }
        finally {
            setBusy(false);
        }
    };

    return (
        <Button
            onClick={handleMicrosoftLogin}
            variant="outlined"
            color="primary"
            fullWidth
            disabled={busy}
            sx={{ textTransform: 'none', height: 48, borderRadius: `${radius.pill}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}
            aria-label={t('Continue with Microsoft', { defaultValue: 'Continue with Microsoft' })}
        >
            <img
                src="/assets/MicrosoftLogo.png"
                alt="Microsoft logo"
                style={{ width: 18, height: 18 }}
            />
            <span>{t('Continue with Microsoft', { defaultValue: 'Continue with Microsoft' })}</span>
        </Button>
    );
};

export { MicrosoftAuth };
